import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

// ant design icons
import EyeOutlined from '@ant-design/icons/EyeOutlined';

// project imports
import useAuth from 'hooks/useAuth';
import { openSnackbar } from 'api/snackbar';

// Tall enough to read at a glance, short enough not to eat a row of the table
// underneath. The page reserves exactly this much so nothing ends up hidden
// behind it.
const BAR_HEIGHT = 52;

// ==============================|| IMPERSONATION BANNER ||============================== //

/**
 * A standing reminder that this session is not your own.
 *
 * Deliberately loud and impossible to dismiss. The failure mode this exists to
 * prevent is an administrator forgetting they are someone else and writing a
 * clinical note, a diary reply or a consent record under that person's name —
 * which the audit trail would then attribute to them. A subtle indicator does
 * not survive a long support call; this one does.
 *
 * TWO DEVICES, because one is not enough. A bar pinned to the BOTTOM stays in
 * view no matter how far down a long record the user has scrolled, where a
 * banner at the top scrolls away exactly when a form is being filled in. And a
 * tinted frame around the whole viewport colours the entire screen, so the
 * state is legible from peripheral vision rather than needing to be read.
 *
 * Rendered above everything, on every route, because impersonation is a
 * property of the SESSION rather than of any screen.
 */
export default function ImpersonationBanner() {
  const intl = useIntl();
  const { isImpersonating, impersonatorEmail, user, endImpersonation } = useAuth();
  const [leaving, setLeaving] = useState(false);

  /* An impersonation that simply runs out its 30 minutes ends without anyone
   * pressing Stop. utils/axios puts the actor back in their own session and
   * reloads with this marker; without a word here the admin would find
   * themselves silently back as themselves, mid-task, and reasonably wonder
   * whether anything they just did was saved. Stripped from the URL so a
   * refresh does not repeat it. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('impersonation') !== 'expired') return;
    openSnackbar({
      open: true,
      message: intl.formatMessage({ id: 'impersonation.expired' }),
      variant: 'alert',
      alert: { color: 'warning' }
    });
    params.delete('impersonation');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [intl]);

  /* Reserve the bar's height at the foot of the document so the last row of a
   * table or the Save button of a form cannot sit underneath it. Undone on the
   * way out, including when the component unmounts because impersonation ended
   * — otherwise the gap would outlive the bar that justified it. */
  useEffect(() => {
    if (!isImpersonating) return undefined;
    const previous = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `${BAR_HEIGHT}px`;
    return () => {
      document.body.style.paddingBottom = previous;
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const exit = async () => {
    setLeaving(true);
    try {
      await endImpersonation();
    } catch {
      // The token carries the actor, so the only ways this fails are a dead
      // network or a deactivated actor account. Re-enable the button rather
      // than trapping them in the session.
      setLeaving(false);
    }
  };

  const viewing = user?.name || user?.email || intl.formatMessage({ id: 'impersonation.anotherUser' });
  const viewingEmail = user?.name && user?.email ? user.email : null;

  return (
    <>
      {/* The frame. Fixed and click-through, so it tints the screen without
          taking a single pixel from the layout or swallowing a click. */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          border: 3,
          borderColor: 'warning.main',
          zIndex: (theme) => theme.zIndex.drawer + 3
        }}
      />

      <Box
        role="status"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          minHeight: BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1.5, sm: 2.5 },
          py: 0.75,
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          boxShadow: (theme) => `0 -2px 12px ${alpha(theme.palette.common.black, 0.18)}`,
          zIndex: (theme) => theme.zIndex.drawer + 4
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%' }} flexWrap="wrap" useFlexGap>
          <EyeOutlined />
          <Typography variant="body2" sx={{ minWidth: 0 }}>
            {intl.formatMessage({ id: 'impersonation.banner' }, { name: <strong>{viewing}</strong> })}
            {viewingEmail && (
              <Box component="span" sx={{ opacity: 0.85 }}>
                {' '}
                ({viewingEmail})
              </Box>
            )}
          </Typography>

          {/* Which of your own accounts is driving. Matters when an admin holds
              more than one, and costs nothing to show. */}
          {impersonatorEmail && (
            <Typography variant="caption" sx={{ opacity: 0.85, display: { xs: 'none', md: 'block' } }}>
              · {intl.formatMessage({ id: 'impersonation.signedInAs' }, { email: impersonatorEmail })}
            </Typography>
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="contained"
            color="inherit"
            disabled={leaving}
            onClick={exit}
            startIcon={leaving ? <CircularProgress size={14} color="inherit" /> : <EyeOutlined />}
            sx={{ color: 'warning.dark', bgcolor: 'background.paper', flexShrink: 0, '&:hover': { bgcolor: 'background.paper' } }}
          >
            {intl.formatMessage({ id: leaving ? 'impersonation.exiting' : 'impersonation.exit' })}
          </Button>
        </Stack>
      </Box>
    </>
  );
}
